import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/layouts/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Filter } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import BlogResultsSection from "@/components/features/blogs/BlogResultsSection";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSearchTerm } from "@/hooks/use-search-term";
import SearchBarSection from "@/components/sections/SearchBarSection";
import { useBlogsData } from "@/hooks/use-blogs-data";
import Pagination from "@/components/ui/pagination";
import BlogCategorySkeleton from "@/components/loaders/BlogCategorySkeleton";

const itemsPerPage = 6;

const BlogCategories = () => {
  const { t, language } = useLanguage();
  const { searchTerm, setSearchTerm } = useSearchTerm(200);
  const {
    blogs,
    categories,
    selectedCategoryId,
    currentPage,
    totalPosts,
    loading,
    setCurrentPage,
    setSelectedCategoryId,
  } = useBlogsData(itemsPerPage);

  const [categoryFilter, setCategoryFilter] = useState("");

  const filteredCategories = [
    { id: "All", title: language === "ar" ? "الكل" : "All" },
    ...categories.filter((cat) =>
      cat.title.toLowerCase().includes(categoryFilter.toLowerCase())
    ),
  ];

  const filteredBlogs = blogs.filter((blog) => {
    const matchesSearch =
      blog.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.tags?.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory =
      selectedCategoryId === "All" ||
      blog.categories?.some((cat) => cat?.id === selectedCategoryId);
    return matchesSearch && matchesCategory;
  });

  const selectedCategoryTitle =
    selectedCategoryId === "All"
      ? "All"
      : categories.find((cat) => cat.id === selectedCategoryId)?.title || "";

  return (
    <Layout>
      {/* Top Section: Always visible */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-white to-gray-50 dark:from-nexus-dark dark:to-gray-900">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <Button variant="ghost" asChild className="mb-6 text-nexus-teal hover:text-nexus-teal/80">
              <Link to="/blog">{t("blog_categories.backToBlog")}</Link>
            </Button>
            <h1 className="text-4xl md:text-5xl font-bold text-nexus-gray dark:text-white mb-6">
              {t("blog_categories.browseByCategory")}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
              {t("blog_categories.introText")}
            </p>
            <SearchBarSection
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              placeholder={t("blog_search_bar")}
            />
          </div>
        </div>
      </section>

      {/* Grid Section: Conditionally show loader or real content */}
      <section className="py-16 bg-white dark:bg-nexus-dark">
        <div className="container">
          {loading ? (
            <BlogCategorySkeleton />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Categories */}
              <div className="lg:col-span-1 self-start mt-12">
                <Card className="sticky top-6">
                  <CardHeader>
                    <CardTitle>{t("blog_categories.categories")}</CardTitle>
                    <CardDescription>{t("blog_categories.filterDescription")}</CardDescription>
                    <div className="relative">
                      <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        type="text"
                        placeholder={t("blog_categories.categoryFilterPlaceholder")}
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className="pl-9 text-sm"
                      />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[600px] pr-4">
                      <div className="space-y-2" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                        {filteredCategories.map((cat) => (
                          <button
                            key={cat.id}
                            onClick={() => setSelectedCategoryId(cat.id)}
                            className={`w-full text-left p-3 rounded-lg transition-colors flex items-center justify-between ${
                              selectedCategoryId === cat.id
                                ? "bg-nexus-brand text-white"
                                : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                            }`}
                          >
                            <span className="text-sm font-medium">{cat.title}</span>
                          </button>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>

              {/* Blogs */}
              <div className="lg:col-span-3">
                <BlogResultsSection
                  blogs={filteredBlogs}
                  totalCount={totalPosts}
                  searchTerm={searchTerm}
                  selectedCategory={selectedCategoryTitle}
                  onClearFilters={() => {
                    setSearchTerm("");
                    setSelectedCategoryId("All");
                    setCategoryFilter("");
                  }}
                  columns="lg:grid-cols-2"
                />
                <div className="flex justify-center mt-8">
                  <Pagination
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    totalPages={Math.ceil(totalPosts / itemsPerPage)}
                    onPageChange={(page) => setCurrentPage(page)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default BlogCategories;
